import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BookmarkPlus } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import type { AdSet } from '../../../types/metaAdDraft';
import { createSavedAudience, listSavedAudiences } from '../api';
import {
  applySavedAudience,
  buildSavedAudienceSource,
  defaultSavedAudienceName,
} from '../savedAudience';

// Saved Audiences (Feature B, Phase 1) — per-ad-set in-flow controls.
//   Save: captures THIS ad set's current targeting → SavedAudience (name prompt,
//         default from the source/account). Server strips placements.
//   Apply: drops a saved audience's targeting into this ad set (a seed; fully
//          editable after, carries no placements).
export function SavedAudienceControls({
  accountId,
  accountName,
  fbAdAccountId,
  adSet,
  onChange,
}: {
  accountId: string;
  accountName: string;
  fbAdAccountId: string | null;
  adSet: AdSet;
  onChange: (next: AdSet) => void;
}) {
  const qc = useQueryClient();
  const enabled = accountId.trim().length > 0;

  const list = useQuery({
    queryKey: ['savedAudiences', accountId],
    queryFn: () => listSavedAudiences(accountId),
    enabled,
  });
  const audiences = list.data?.savedAudiences ?? [];

  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');

  const save = useMutation({
    mutationFn: (n: string) =>
      createSavedAudience(accountId, {
        name: n,
        targeting: adSet.targeting,
        source: buildSavedAudienceSource(adSet.targeting, fbAdAccountId),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['savedAudiences', accountId] });
      setSaveOpen(false);
      toast.success('Audience saved to account');
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not save audience.'),
  });

  const openSave = () => {
    setName(
      defaultSavedAudienceName(adSet.targeting, {
        accountName,
        today: new Date().toISOString().slice(0, 10),
      }),
    );
    setSaveOpen(true);
  };

  const apply = (id: string) => {
    const aud = audiences.find((a) => a.id === id);
    if (!aud) return;
    onChange(applySavedAudience(adSet, aud));
    toast.success(`Applied "${aud.name}"`);
  };

  if (!enabled) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md bg-slate-50 px-3 py-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Saved audiences
      </span>

      <button
        type="button"
        onClick={openSave}
        className="inline-flex items-center gap-1 text-xs font-medium text-kyck-700 hover:text-kyck-800"
      >
        <BookmarkPlus className="h-3.5 w-3.5" /> Save audience to account
      </button>

      {audiences.length > 0 && (
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          Use saved audience
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) apply(e.target.value);
            }}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
          >
            <option value="">— select —</option>
            {audiences.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {saveOpen && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4"
          onClick={() => !save.isPending && setSaveOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-slate-800">Save audience to account</h3>
            <p className="mt-2 text-sm text-slate-600">
              Saves this ad set's targeting as a reusable audience on{' '}
              <span className="font-medium">{accountName || 'this account'}</span>. Placements
              aren't saved — kyck's placement setting always controls those.
            </p>
            <label
              htmlFor={`save-audience-name-${adSet.id}`}
              className="mb-1.5 mt-4 block text-sm font-medium text-slate-700"
            >
              Audience name
            </label>
            <input
              id={`save-audience-name-${adSet.id}`}
              type="text"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !save.isPending && name.trim() !== '') save.mutate(name.trim());
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={save.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => save.mutate(name.trim())}
                disabled={save.isPending || name.trim() === ''}
              >
                {save.isPending ? 'Saving…' : 'Save audience'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
